import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Avatar,
  Stack,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Edit,
  Person,
  LocationOn,
  Assignment,
  AccountBalance,
  Close as CloseIcon,
} from "@material-ui/icons";

// Import existing modal components
import ChangeSubDealerParent from "../../../../components/dealercomps/subdealers/ChangeSubDealerParent";
import DealerContractsForm from "../../../../components/manage-dealers/DealerContractsForm";
import ChangeDealerPassword from "../../../../components/manage-dealers/dealer-detail-r/ChangeDealerPassword";
import SupportModal from "../../../../components/manage-dealers/dealer-detail-r/financeApplications/SupportModal";
import CreditModal from "../../../../components/manage-dealers/dealer-detail-r/financeApplications/CreditModal";
import SendAlertModal from "../../../../components/manage-dealers/dealer-detail-r/SendAlertModal";
import SelectSuppliers from "../../../../components/manage-dealers/SelectSuppliers";
import ConfirmationDialog from "../../../../components/global/ConfirmationDialog";
import ChangeUserTypeModal from "../shared/ChangeUserTypeModal";

// eslint-disable-next-line jsdoc/require-param
/**
 * The UserDetailModals component displays a variety of modals for the user detail page.
 *
 * It takes the following props:
 * - dealer: The user object containing user details.
 * - editDealerOpen: A boolean indicating whether the edit dialog is open.
 * - onCloseEdit: The function to call when the edit dialog is closed.
 * - editTabValue: The value of the edit dialog tab.
 * - onEditTabChange: The function to call when the edit dialog tab is changed.
 * - editError: An error message to display if the edit dialog encounters an error.
 * - renderEditTabContent: A function that renders the content of the edit dialog based on the current tab value.
 * - contractsOpen: A boolean indicating whether the contracts modal is open.
 * - onCloseContracts: The function to call when the contracts modal is closed.
 * - changePasswordOpen: A boolean indicating whether the change password modal is open.
 * - onCloseChangePassword: The function to call when the change password modal is closed.
 * - changeParentOpen: A boolean indicating whether the change parent modal is open.
 * - onCloseChangeParent: The function to call when the change parent modal is closed.
 * - suppliersModalOpen: A boolean indicating whether the suppliers modal is open.
 * - onCloseSuppliersModal: The function to call when the suppliers modal is closed.
 * - supportModalOpen: A boolean indicating whether the support modal is open.
 * - onCloseSupportModal: The function to call when the support modal is closed.
 * - creditModalOpen: A boolean indicating whether the credit modal is open.
 * - onCloseCreditModal: The function to call when the credit modal is closed.
 * - alertModalOpen: A boolean indicating whether the alert modal is open.
 * - onCloseAlertModal: The function to call when the alert modal is closed.
 * - showDeleteConfirmation: A boolean indicating whether the delete confirmation modal is open.
 * - onCloseDeleteConfirmation: The function to call when the delete confirmation modal is closed.
 * - onConfirmDelete: The function to call when the delete confirmation modal is confirmed.
 * - deletingDealer: A boolean indicating whether the delete confirmation modal is in a loading state.
 * - onRefreshDetails: The function to call when the user's details need to be refreshed.
 */
export default function UserDetailModals({
  dealer,
  editDealerOpen,
  onCloseEdit,
  editTabValue,
  onEditTabChange,
  editError,
  renderEditTabContent,
  contractsOpen,
  onCloseContracts,
  changePasswordOpen,
  onCloseChangePassword,
  changeParentOpen,
  onCloseChangeParent,
  suppliersModalOpen,
  onCloseSuppliersModal,
  supportModalOpen,
  onCloseSupportModal,
  creditModalOpen,
  onCloseCreditModal,
  alertModalOpen,
  onCloseAlertModal,
  showDeleteConfirmation,
  onCloseDeleteConfirmation,
  onConfirmDelete,
  deletingDealer,
  onRefreshDetails,
  userTypeModalOpen,
  onCloseUserTypeModal,
}) {
  return (
    <>
      {/* Enhanced Edit Dialog */}
      <Dialog
        open={editDealerOpen}
        onClose={onCloseEdit}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: "70vh",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: "primary.main" }}>
                <Edit />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Edit User: {dealer.first_name} {dealer.surname}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update user information across multiple sections
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={onCloseEdit} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Tabs
            value={editTabValue}
            onChange={onEditTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 48,
              },
            }}
          >
            <Tab
              label="Personal"
              icon={<Person fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Address"
              icon={<LocationOn fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Documents"
              icon={<Assignment fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Banking"
              icon={<AccountBalance fontSize="small" />}
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {editError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {editError}
              </Alert>
            )}
            {renderEditTabContent(editTabValue)}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Change Parent Modal */}
      <ChangeSubDealerParent
        open={changeParentOpen}
        close={onCloseChangeParent}
        dealer={dealer}
      />

      {/* Contracts Modal */}
      <DealerContractsForm
        dialogIsOpen={contractsOpen}
        closeDialog={onCloseContracts}
        dealerProp={dealer}
        editing
      />

      {/* Change Password Modal */}
      <ChangeDealerPassword
        dealerId={dealer.auth_id}
        open={changePasswordOpen}
        close={onCloseChangePassword}
      />

      {/* Support Modal */}
      <SupportModal
        open={supportModalOpen}
        onClose={onCloseSupportModal}
        dealer={dealer}
      />

      {/* Credit Modal */}
      <CreditModal
        open={creditModalOpen}
        onClose={onCloseCreditModal}
        dealer={dealer}
      />

      {/* Alert Modal */}
      <SendAlertModal
        open={alertModalOpen}
        onClose={onCloseAlertModal}
        dealer={dealer}
      />

      {/* Suppliers Modal */}
      <Dialog
        fullWidth
        maxWidth="md"
        open={suppliersModalOpen}
        onClose={onCloseSuppliersModal}
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Suppliers and Product Types
          </Typography>
        </DialogTitle>
        <DialogContent>
          <SelectSuppliers
            userId={dealer.id}
            onClose={() => {
              onCloseSuppliersModal();
              onRefreshDetails();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationDialog
        open={showDeleteConfirmation}
        message="Are you sure you want to delete this user? This action cannot be undone."
        onConfirm={onConfirmDelete}
        onClose={onCloseDeleteConfirmation}
        loading={deletingDealer}
      />

      {/* User Type Modal */}
      <ChangeUserTypeModal
        open={userTypeModalOpen}
        onClose={onCloseUserTypeModal}
        dealer={dealer}
        onRefresh={onRefreshDetails}
      />
    </>
  );
}
